/**
 * Generic pill-style dropdown switcher.
 *
 * Accessible listbox with keyboard navigation, open/close transitions, and
 * brand styling. Use for dataset pickers and similar two-or-more-option toggles.
 */

import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";

import { ChevronDown, type LucideIcon } from "lucide-react";

export type DropdownSwitcherOption<T extends string = string> = {
  value: T;
  label: string;
  icon?: LucideIcon;
};

export type DropdownSwitcherProps<T extends string = string> = {
  value: T;
  options: DropdownSwitcherOption<T>[];
  /** Prefix before the selected label, e.g. `Dataset: `. */
  prefix?: string;
  /** Called only when the value actually changes. */
  onChange?: (value: T) => void;
  className?: string;
  /** When true, the trigger cannot open the menu. */
  disabled?: boolean;
  /** Override trigger aria-label. Defaults to `{prefix}{label}. Change selection.` */
  ariaLabel?: string;
  /** Listbox aria-label. Defaults to prefix without trailing colon or "Options". */
  listboxLabel?: string;
};

export function DropdownSwitcher<T extends string>({
  value,
  options,
  prefix,
  onChange,
  className = "",
  disabled = false,
  ariaLabel,
  listboxLabel,
}: DropdownSwitcherProps<T>) {
  const [open, setOpen] = useState(false);
  /** Keep the menu mounted through the close transition. */
  const [menuMounted, setMenuMounted] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(
      0,
      options.findIndex(option => option.value === value)
    )
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const listboxId = useId();

  const selected = options.find(option => option.value === value) ?? options[0];
  const TriggerIcon = selected?.icon;

  useEffect(() => {
    if (open) {
      setActiveIndex(
        Math.max(
          0,
          options.findIndex(option => option.value === value)
        )
      );
      setMenuMounted(true);
      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => setMenuVisible(true));
      });
      return () => cancelAnimationFrame(frame);
    }
    setMenuVisible(false);
    const timer = window.setTimeout(() => setMenuMounted(false), 200);
    return () => window.clearTimeout(timer);
  }, [open, value, options]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  useEffect(() => {
    if (!menuVisible) return;
    optionRefs.current[activeIndex]?.focus();
  }, [menuVisible, activeIndex]);

  const choose = (next: T) => {
    setOpen(false);
    triggerRef.current?.focus();
    if (next === value) return;
    onChange?.(next);
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
    }
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key === "Tab") {
      setOpen(false);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex(index => (index + 1) % options.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex(index => (index - 1 + options.length) % options.length);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(options.length - 1);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      choose(options[activeIndex].value);
    }
  };

  const triggerAriaLabel =
    ariaLabel ??
    `${prefix ?? ""}${selected?.label ?? value}. Change selection.`.trim();

  const menuAriaLabel =
    listboxLabel ?? prefix?.replace(/:\s*$/, "") ?? "Options";

  if (!options.length) return null;

  const hasWidthClass = /\bw-/.test(className);
  const rootClassName = [
    "relative inline-block",
    hasWidthClass ? null : "w-fit",
    className || null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClassName} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen(current => !current);
        }}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={triggerAriaLabel}
        className={[
          "inline-flex h-9 w-full min-w-0 items-center gap-2 rounded-xl px-3.5",
          "bg-white text-sm font-medium text-gray-700",
          "transition-[border-color,background-color,box-shadow] duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/40",
          disabled
            ? "cursor-not-allowed border border-gray-200 opacity-50"
            : open
              ? "border border-[#c4b5fd] shadow-[0_0_0_3px_rgba(151,74,228,0.12)]"
              : "border border-gray-200 hover:border-gray-300 hover:bg-gray-50",
        ].join(" ")}
      >
        {TriggerIcon ? (
          <TriggerIcon className="h-3.5 w-3.5 shrink-0 text-gray-500" />
        ) : null}
        <span className="min-w-0 flex-1 truncate text-left whitespace-nowrap">
          {prefix ? (
            <>
              {prefix}
              <span className="font-semibold text-gray-900">
                {selected?.label ?? value}
              </span>
            </>
          ) : (
            <span className="font-semibold text-gray-900">
              {selected?.label ?? value}
            </span>
          )}
        </span>
        <ChevronDown
          aria-hidden
          className={`h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform duration-200 ease-out ${
            open ? "rotate-180" : "rotate-0"
          }`}
        />
      </button>

      {menuMounted && (
        <div
          id={listboxId}
          role="listbox"
          aria-label={menuAriaLabel}
          aria-activedescendant={`${listboxId}-${options[activeIndex]?.value}`}
          onKeyDown={onMenuKeyDown}
          className={[
            "absolute left-0 z-50 mt-2 max-h-60 min-w-full origin-top overflow-y-auto",
            "rounded-xl border border-gray-200 bg-white p-1 shadow-lg",
            "transition-[opacity,transform] duration-200 ease-out",
            "motion-reduce:transition-none",
            "[scrollbar-width:thin] [scrollbar-color:#d1d5db_transparent]",
            menuVisible
              ? "translate-y-0 scale-100 opacity-100"
              : "-translate-y-1.5 scale-[0.98] opacity-0",
          ].join(" ")}
        >
          <ul className="m-0 list-none p-0">
            {options.map((option, index) => {
              const isSelected = option.value === value;
              const isHighlighted = index === activeIndex;
              const Icon = option.icon;
              return (
                <li key={option.value}>
                  <button
                    ref={node => {
                      optionRefs.current[index] = node;
                    }}
                    id={`${listboxId}-${option.value}`}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={isHighlighted ? 0 : -1}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => choose(option.value)}
                    className={[
                      "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2",
                      "text-left text-sm font-medium transition-colors duration-150",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/30",
                      isSelected
                        ? "bg-[#f4efff] text-[#974ae4]"
                        : "text-gray-800 hover:bg-gray-50",
                      !isSelected && isHighlighted ? "bg-gray-50" : "",
                    ].join(" ")}
                  >
                    {Icon ? (
                      <Icon
                        aria-hidden
                        className={`h-4 w-4 shrink-0 ${
                          isSelected ? "text-[#974ae4]" : "text-gray-500"
                        }`}
                      />
                    ) : null}
                    {option.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default DropdownSwitcher;
